import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import adminApi, { errMsg } from '../../api';
import { useAdminAuth, hasRole } from '../../auth';
import { useRange, withRange } from '../../range';
import { usePageParams } from '../../paging';
import { Card } from '../../components/Card';
import DataTable from '../../components/DataTable';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import Pagination from '../../components/Pagination';
import StatusPill from '../../components/StatusPill';
import { formatCompactNumber, formatPct } from '../../format';
import useSort, { applySort } from '../../hooks/useSort';

/**
 * Catalog › Brands — the list, as a way in.
 *
 * This used to be two pages that each knew half the story: "Brands" listed
 * allowlist status and held the onboarding form, "Catalog" held engagement
 * and the funnel, and neither linked to the other. One list now, with every
 * row a link to that brand's own page where crawl status, products,
 * moderation and the funnel all live together.
 *
 * Onboarding stays here because it belongs to the list, not to any one brand.
 * Deboarding moved to the brand page: it needs a reason, it pulls the domain
 * from the buy-click redirect allowlist, and it deserves the context of what
 * that brand is currently doing rather than a text box in a row.
 */
const emptyForm = {
  name: '', domain: '', gender_hint: '', category_hint: '',
  listing_urls: '', affiliate_param_name: '', affiliate_param_value: '',
};

const input = 'rounded-lg border border-hairline px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-raspberry-300';

export default function BrandsPage() {
  const { admin } = useAdminAuth();
  const navigate = useNavigate();
  const range = useRange();
  const canEdit = hasRole(admin, 'operator', 'owner');

  const { sort, onSort } = useSort();

  // Declared before the sorted/paged rows below, which reference it — a
  // `const` referenced before its own declaration throws at runtime, it is
  // not hoisted the way a `function`/`var` would be.
  const columns = useMemo(() => [
    {
      key: 'name',
      label: 'Brand',
      sortable: true,
      render: (r) => (
        <div className="min-w-0">
          <div className="font-medium text-ink">{r.name}</div>
          <div className="truncate text-[11px] text-ink-muted">{(r.domains || []).join(', ') || r.brand_id}</div>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (r) => {
        if (!r.onboarded) {
          // Crawled but never added to the redirect allowlist — every buy
          // click from this brand is unattributable, which is worth its own
          // word rather than an empty cell.
          return <StatusPill tone="warning" label="Not onboarded" />;
        }
        return r.active
          ? <StatusPill tone="good" label="Allowlisted" />
          : <StatusPill tone="critical" label="Deboarded" />;
      },
    },
    {
      key: 'crawl',
      label: 'Crawl',
      render: (r) => (r.source?.enabled
        ? <StatusPill tone="good" label="On" />
        : <StatusPill tone="neutral" label="Off" />),
    },
    { key: 'catalog_size', label: 'Catalogue', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.catalog_size ?? 0) },
    { key: 'impressions', label: 'Impressions', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.impressions ?? 0) },
    { key: 'saves', label: 'Saved', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.saves ?? 0) },
    { key: 'passes', label: 'Passed', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.passes ?? 0) },
    {
      key: 'save_rate',
      label: 'Impr → Save',
      align: 'right',
      sortable: true,
      render: (r) => formatPct(r.funnel_pct?.impression_to_save, 1),
    },
    { key: 'billable_clicks', label: 'Billable clicks', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.billable_clicks ?? 0) },
  ], []);

  const { page, pageSize, setPage, setPageSize } = usePageParams();
  const [params, setSearchParams] = useSearchParams();
  const query = params.get('q') || '';

  const [brands, setBrands] = useState([]);
  const [engagement, setEngagement] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOnboard, setShowOnboard] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Sets `q` and clears `page` in ONE update, not two. `usePageParams`'s own
  // `resetPage` uses a separate `useSearchParams()` instance; calling it
  // right after this hook's own `setSearchParams` would have each read a
  // `prev` that does not yet include the other's change, so one write would
  // silently lose the other — the same hazard ProductsPage's sort/order
  // change already hit once before this file existed.
  const setQuery = (value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set('q', value);
      else next.delete('q');
      next.delete('page'); // a narrower or wider result set makes the old page number meaningless
      return next;
    }, { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Two sources, one table: `/ops/brands` is the allowlist (who is
      // onboarded, whose crawl is on), `/catalog/brands` is the engagement
      // rollup (catalogue size, what people did with it). A brand can appear
      // in one and not the other, and which one is itself the finding.
      // Both lists in full, then merged and paged locally.
      //
      // Server-side paging is wrong for this one table specifically: the row
      // set is the UNION of the allowlist and the engagement rollup, and a
      // brand can be in either alone — onboarded but with nothing crawled
      // yet, or crawled but never onboarded (which the table flags, since its
      // buy clicks are unattributable). Paging one source server-side would
      // silently drop the other's extras from the page.
      //
      // This is safe because brands number in the tens, and because the
      // expensive part of `/catalog/brands` — a full `inventory` scan for the
      // per-brand counts — is now cached server-side. Pagination here is for
      // readability, not for load.
      const [brandsResp, engagementResp] = await Promise.all([
        adminApi.get('/ops/brands'),
        adminApi.get('/catalog/brands', {
          params: { days: range.days, page_size: 500 },
        }).catch(() => null),
      ]);
      setBrands(brandsResp.data.items || []);
      setEngagement(engagementResp?.data?.items || []);
    } catch (err) {
      toast.error(errMsg(err, 'Could not load brands'));
    } finally {
      setLoading(false);
    }
  }, [range.days]);

  useEffect(() => { load(); }, [load]);

  const allRows = useMemo(() => {
    const byId = new Map();
    engagement.forEach((e) => byId.set(e.brand_id, e));
    brands.forEach((b) => {
      byId.set(b.brand_id, { ...(byId.get(b.brand_id) || {}), ...b, brand_id: b.brand_id, onboarded: true });
    });
    return Array.from(byId.values()).map((r) => ({
      ...r,
      name: r.name || r.brand_id,
      onboarded: r.onboarded === true || brands.some((b) => b.brand_id === r.brand_id),
    })).sort((a, b) => (b.impressions || 0) - (a.impressions || 0));
  }, [brands, engagement]);

  // Filtered client-side, same reasoning as pagination above: the merged
  // list is already fully in memory, so a text filter is a plain array
  // method rather than another request.
  const rows = useMemo(() => {
    if (!query.trim()) return allRows;
    const q = query.trim().toLowerCase();
    return allRows.filter((r) => (
      (r.name || '').toLowerCase().includes(q)
      || (r.domains || []).some((d) => d.toLowerCase().includes(q))
      || (r.brand_id || '').toLowerCase().includes(q)
    ));
  }, [allRows, query]);

  // Local paging over the (filtered) merged list. `meta` is shaped exactly
  // like the server's envelope so <Pagination> cannot tell the difference.
  const sortedRows = useMemo(
    () => applySort(rows, sort, columns),
    [rows, sort, columns],
  );
  const pageRows = useMemo(
    () => sortedRows.slice((page - 1) * pageSize, page * pageSize),
    [sortedRows, page, pageSize],
  );
  const meta = useMemo(() => ({
    page,
    page_size: pageSize,
    total: sortedRows.length,
    total_pages: Math.max(1, Math.ceil(sortedRows.length / pageSize)),
    has_more: page * pageSize < sortedRows.length,
  }), [sortedRows.length, page, pageSize]);

  const onboard = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminApi.post('/ops/brands', {
        name: form.name,
        domain: form.domain,
        gender_hint: form.gender_hint || null,
        category_hint: form.category_hint || null,
        listing_urls: form.listing_urls
          ? form.listing_urls.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
        affiliate_param_name: form.affiliate_param_name,
        affiliate_param_value: form.affiliate_param_value,
        enabled: true,
      });
      toast.success(`${form.name} onboarded — added to the redirect allowlist and queued for crawling`);
      setForm(emptyForm);
      setShowOnboard(false);
      await load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not onboard brand'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Brands"
        hint={`Catalogue and engagement per brand — ${range.label.toLowerCase()}. Open one for its crawl, products and funnel.`}
        actions={canEdit && (
          <button
            onClick={() => setShowOnboard((v) => !v)}
            className="rounded-lg bg-raspberry-600 px-3 py-2 text-sm font-medium text-white hover:bg-raspberry-700"
          >
            {showOnboard ? 'Cancel' : 'Onboard brand'}
          </button>
        )}
      />

      {showOnboard && (
        <Card title="Onboard a brand" hint="One action: registers the crawl source and adds the domain to the buy-click redirect allowlist." className="mb-6">
          <form onSubmit={onboard} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <input required placeholder="Brand name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
            <input required placeholder="Domain (e.g. westside.com)" value={form.domain}
              onChange={(e) => setForm({ ...form, domain: e.target.value })} className={input} />
            <select value={form.gender_hint} onChange={(e) => setForm({ ...form, gender_hint: e.target.value })} className={input}>
              <option value="">Gender hint — infer per product</option>
              <option value="female">Women's</option>
              <option value="male">Men's</option>
            </select>
            <input placeholder="Category hint (optional)" value={form.category_hint}
              onChange={(e) => setForm({ ...form, category_hint: e.target.value })} className={input} />
            <input placeholder="Listing URLs, comma-separated (optional — defaults to the homepage)"
              value={form.listing_urls} onChange={(e) => setForm({ ...form, listing_urls: e.target.value })}
              className={`${input} sm:col-span-2`} />
            <input placeholder="Affiliate param name (optional)" value={form.affiliate_param_name}
              onChange={(e) => setForm({ ...form, affiliate_param_name: e.target.value })} className={input} />
            <input placeholder="Affiliate param value (optional)" value={form.affiliate_param_value}
              onChange={(e) => setForm({ ...form, affiliate_param_value: e.target.value })} className={input} />
            <button type="submit" disabled={saving}
              className="rounded-lg bg-raspberry-600 px-4 py-2 text-sm font-medium text-white hover:bg-raspberry-700 disabled:opacity-50 sm:col-span-2">
              {saving ? 'Onboarding…' : 'Onboard brand'}
            </button>
          </form>
        </Card>
      )}

      <div className="mb-4 flex items-center gap-2">
        <input
          placeholder="Search by brand name or domain"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-72 rounded-lg border border-hairline px-3 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-raspberry-300"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            className="text-xs font-medium text-ink-secondary hover:text-ink"
          >
            Clear ✕
          </button>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={pageRows}
        rowKey={(r) => r.brand_id}
        loading={loading}
        sort={sort}
        onSort={onSort}
        onRowClick={(r) => navigate(withRange(`/brand/${encodeURIComponent(r.brand_id)}`, range))}
        empty={
          <EmptyState
            title={query ? 'No brand matches that' : 'No brands yet'}
            detail={query
              ? 'Search matches brand name and domain.'
              : 'Onboard one directly, or approve a brand application from the Applications tab.'}
          />
        }
      />

      <Pagination meta={meta} loading={loading} onPage={setPage} onPageSize={setPageSize} />
    </div>
  );
}
